/**
 * Mod-only "Search the web (Vision)" reverse-image modal.
 *
 * Triggered from the photo lightbox menu. Fetches
 * GET /v1/mod/photos/{id}/web-detection via ApiClient and shows the distilled
 * Google Vision Web Detection evidence: a strength tier, the best-guess label,
 * the distinct real-human identities the photo is published under, the pages
 * carrying an exact/partial match, and matching-image thumbnails.
 *
 * Mod-only surface, so its body copy is hardcoded English (mirrors
 * photo-reuse-lightbox.js and the moderation i18n carve-out). All
 * server-supplied text (identity names, page titles) is written through
 * `textContent`, never `innerHTML` (the web is adversarial input).
 */
(function () {
    'use strict';

    var modalEl = null;
    var state = {
        photoId: '',
        requestSeq: 0,
        priorBodyOverflow: null,
    };

    function ensureModal() {
        if (modalEl) return modalEl;
        modalEl = document.createElement('div');
        modalEl.id = 'photo-web-detection-lightbox-modal';
        modalEl.className = 'hidden fixed inset-0 z-[10010]';
        modalEl.innerHTML = ''
            + '<div class="pwd-backdrop fixed inset-0 bg-black/50 flex items-end justify-center sm:items-center sm:p-6">'
            + '  <div class="pwd-card bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[90dvh] overflow-hidden shadow-2xl">'
            + '    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">'
            + '      <div>'
            + '        <h2 class="m-0 text-sm font-semibold text-gray-900 dark:text-gray-100">Search the web (Vision)</h2>'
            + '        <p class="m-0 mt-0.5 text-xs text-gray-500 dark:text-gray-400">Where this image appears on the public web, and under what identities.</p>'
            + '      </div>'
            + '      <button type="button" class="pwd-close p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 text-lg leading-none border-0 bg-transparent cursor-pointer" aria-label="Close">&times;</button>'
            + '    </div>'
            + '    <div class="pwd-body flex-1 overflow-y-auto p-3"></div>'
            + '  </div>'
            + '</div>';

        document.body.appendChild(modalEl);

        var backdrop = modalEl.querySelector('.pwd-backdrop');
        var card = modalEl.querySelector('.pwd-card');
        var closeBtn = modalEl.querySelector('.pwd-close');

        backdrop.addEventListener('click', function (e) {
            if (e.target === backdrop) close();
        });
        card.addEventListener('click', function (e) { e.stopPropagation(); });
        closeBtn.addEventListener('click', close);
        document.addEventListener('keydown', function (e) {
            if (!modalEl.classList.contains('hidden') && e.key === 'Escape') close();
        });

        return modalEl;
    }

    function bodyHost() {
        return modalEl.querySelector('.pwd-body');
    }

    function clearHost() {
        var host = bodyHost();
        while (host.firstChild) host.removeChild(host.firstChild);
        return host;
    }

    function renderMessage(text) {
        var host = clearHost();
        var p = document.createElement('p');
        p.className = 'm-0 px-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400';
        p.textContent = text;
        host.appendChild(p);
    }

    function renderLoading() {
        var host = clearHost();
        var p = document.createElement('p');
        p.className = 'm-0 px-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400';
        p.textContent = 'Searching the web…';
        host.appendChild(p);
    }

    function strengthBadge(strength) {
        var span = document.createElement('span');
        var base = 'inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ';
        var tone;
        var label;
        if (strength === 'strong') {
            tone = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
            label = 'strong';
        } else if (strength === 'weak') {
            tone = 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
            label = 'weak';
        } else {
            tone = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
            label = 'none';
        }
        span.className = base + tone;
        span.textContent = label;
        return span;
    }

    function domainOf(url) {
        try {
            return new URL(url).hostname.replace(/^www\./, '');
        } catch (e) {
            return url;
        }
    }

    function identityChips(names) {
        var wrap = document.createElement('div');
        wrap.className = 'flex flex-wrap gap-1.5 mt-1';
        names.forEach(function (name) {
            var chip = document.createElement('span');
            chip.className = 'inline-flex items-center px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300 text-[11px] font-medium';
            chip.textContent = name;
            wrap.appendChild(chip);
        });
        return wrap;
    }

    function pageRow(page) {
        var row = document.createElement('a');
        row.className = 'flex flex-col px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 no-underline';
        // The endpoint returns absolute external URLs. Only accept http(s).
        if (/^https?:\/\//i.test(page.url)) {
            row.href = page.url;
            row.target = '_blank';
            row.rel = 'noopener noreferrer';
        }
        var title = document.createElement('span');
        title.className = 'text-sm text-sky-600 dark:text-sky-400 truncate';
        title.textContent = page.title || domainOf(page.url);
        row.appendChild(title);
        var dom = document.createElement('span');
        dom.className = 'text-[11px] text-gray-400 dark:text-gray-500 truncate';
        dom.textContent = domainOf(page.url);
        row.appendChild(dom);
        return row;
    }

    function thumbnailStrip(urls) {
        var wrap = document.createElement('div');
        wrap.className = 'flex flex-wrap gap-2 mt-2';
        urls.forEach(function (url) {
            if (!/^https?:\/\//i.test(url)) return;
            var tile = document.createElement('div');
            tile.className = 'w-14 h-14 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0';
            var img = document.createElement('img');
            img.src = url;
            img.alt = '';
            img.loading = 'lazy';
            img.referrerPolicy = 'no-referrer';
            img.className = 'w-full h-full object-cover';
            tile.appendChild(img);
            wrap.appendChild(tile);
        });
        return wrap;
    }

    function sectionLabel(text) {
        var el = document.createElement('p');
        el.className = 'm-0 mt-3 mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500';
        el.textContent = text;
        return el;
    }

    function renderResult(result) {
        var host = clearHost();

        // Header: strength + best guess.
        var header = document.createElement('div');
        header.className = 'flex items-center gap-2 px-2 py-1';
        header.appendChild(strengthBadge(result.strength));
        if (result.best_guess_label) {
            var guess = document.createElement('span');
            guess.className = 'text-sm text-gray-700 dark:text-gray-200 truncate';
            guess.textContent = 'Best guess: ' + result.best_guess_label;
            header.appendChild(guess);
        }
        host.appendChild(header);

        // Match counts.
        var counts = document.createElement('p');
        counts.className = 'm-0 px-2 text-xs text-gray-500 dark:text-gray-400';
        var full = result.full_match_pages || 0;
        var partial = result.partial_match_pages || 0;
        if (full || partial) {
            counts.textContent = full + (full === 1 ? ' page' : ' pages') + ' with an exact match'
                + (partial ? ', ' + partial + ' partial.' : '.');
        } else {
            counts.textContent = 'No exact or partial web matches. Only "visually similar" noise, or nothing.';
        }
        host.appendChild(counts);

        // Recognized public figure caveat.
        if (result.is_recognized_public_entity) {
            var caveat = document.createElement('p');
            caveat.className = 'm-0 px-2 mt-1 text-[11px] text-amber-600 dark:text-amber-400';
            caveat.textContent = 'Top match is a recognized public figure or meme. Weak signal on its own. Fans legitimately reuse such photos.';
            host.appendChild(caveat);
        }

        // Distinct identities.
        var names = result.distinct_identity_names || [];
        if (names.length) {
            host.appendChild(sectionLabel('Published under ' + names.length
                + (names.length === 1 ? ' identity' : ' distinct identities')));
            var chipsWrap = document.createElement('div');
            chipsWrap.className = 'px-2';
            chipsWrap.appendChild(identityChips(names));
            host.appendChild(chipsWrap);
            if (result.name_conflicts_profile) {
                var conflict = document.createElement('p');
                conflict.className = 'm-0 px-2 mt-1 text-[11px] text-rose-600 dark:text-rose-400';
                conflict.textContent = 'At least one conflicts with this profile\'s stated name.';
                host.appendChild(conflict);
            }
        }

        // Matching pages.
        var pages = result.sample_pages || [];
        if (pages.length) {
            host.appendChild(sectionLabel('Where it appears'));
            var list = document.createElement('div');
            list.className = 'flex flex-col';
            pages.forEach(function (p) { list.appendChild(pageRow(p)); });
            host.appendChild(list);
        }

        // Thumbnails.
        var thumbs = result.thumbnails || [];
        if (thumbs.length) {
            host.appendChild(sectionLabel('Matching images'));
            var thumbWrap = document.createElement('div');
            thumbWrap.className = 'px-2';
            thumbWrap.appendChild(thumbnailStrip(thumbs));
            host.appendChild(thumbWrap);
        }
    }

    function fetchAndRender() {
        renderLoading();

        if (!window.ApiClient) {
            renderMessage('ApiClient unavailable.');
            return;
        }

        var seq = ++state.requestSeq;
        var photoId = state.photoId;

        window.ApiClient.request(
            '/mod/photos/' + encodeURIComponent(photoId) + '/web-detection',
            { method: 'GET' }
        ).then(function (res) {
            if (res && typeof res.json === 'function') {
                return res.json().then(function (body) {
                    return { ok: res.ok, status: res.status, body: body };
                });
            }
            return { ok: true, status: 200, body: res };
        }).then(function (envelope) {
            if (seq !== state.requestSeq || modalEl.classList.contains('hidden')) {
                return;
            }
            if (envelope.ok && envelope.body && envelope.body.status === 'ok' && envelope.body.result) {
                renderResult(envelope.body.result);
                return;
            }
            if (envelope.ok && envelope.body && envelope.body.status === 'unavailable') {
                renderMessage('Reverse web search is unavailable right now (not configured, disabled, or the fetch failed).');
                return;
            }
            renderMessage(
                (envelope.body && envelope.body.error) ? envelope.body.error : 'Search failed.'
            );
        }).catch(function (err) {
            if (seq !== state.requestSeq || modalEl.classList.contains('hidden')) {
                return;
            }
            renderMessage((err && err.message) ? err.message : 'Network error.');
        });
    }

    function open(photoId) {
        ensureModal();
        state.photoId = String(photoId || '');
        modalEl.classList.remove('hidden');
        if (typeof document !== 'undefined' && document.body) {
            state.priorBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        fetchAndRender();
    }

    function close() {
        if (!modalEl) return;
        modalEl.classList.add('hidden');
        state.requestSeq++;
        state.photoId = '';
        if (typeof document !== 'undefined' && document.body) {
            document.body.style.overflow = state.priorBodyOverflow || '';
            state.priorBodyOverflow = null;
        }
    }

    window.openPhotoWebDetectionModal = open;
})();
